/**
 * Capa de adaptadores ANC × AquaOps (Fase 1 de la fusión).
 *
 * El frontend ANC consume rutas semánticas propias (`/lora`,
 * `/observability`, …).  Los backends reales de AquaOps exponen otras
 * (`/api/rf/kpis`, `/api/rf/gateways/status`, …).  En vez de reescribir
 * las vistas, esta capa traduce cada ruta semántica al/los endpoint(s)
 * real(es) y normaliza la respuesta al shape que la vista espera.
 *
 * Activación: cuando `VITE_ANC_RF_BASE` está definido (p.ej. `/rf-api`),
 * las rutas con adaptador van al backend real; el resto cae al mock.
 * Si una llamada al backend real falla, se degrada al mock (la demo
 * nunca se rompe).
 */
import type { ApiRequestInit } from "./client";
import { mockApi } from "./mock";
import type { OperationalInsight, LiveEvent } from "./mock";

/** Base del rf-api (vía proxy Vite o ALB). Vacío = sin backend RF. */
const RF_BASE: string =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_ANC_RF_BASE) || "";

/** ¿Hay backend RF configurado? */
export function hasRfBackend(): boolean {
  return RF_BASE.length > 0;
}

async function rfGet<T>(path: string): Promise<T> {
  const res = await fetch(`${RF_BASE}${path}`, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`RF ${res.status} on ${path}`);
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Tipos crudos del rf-api (subconjunto usado por los adaptadores)
// ---------------------------------------------------------------------------

interface RfExecKpis {
  healthy_pct: number;
  degrading_pct: number;
  critical_pct: number;
  offline_pct: number;
  total_devices: number;
  global_loss_pct: number;
  global_redundancy: number;
}

interface RfGatewayStatus {
  gateway_eui: string;
  last_uplink: string | null;
  devices_served: number;
  total_uplinks: number;
  avg_rssi: number;
  avg_snr: number;
  avg_loss_pct: number;
  status: string;
}

interface RfDeviceHealth {
  dev_eui: string;
  marca: string;
  category: string;
  loss_pct: number;
  avg_margin: number;
  avg_rssi: number;
  last_uplink: string | null;
}

interface RfSfRow {
  sf: number;
  count: number;
}

interface RfProblem {
  type: string;
  entity_type: string;
  entity_id: string;
  severity: string;
  description: string;
  metrics: Record<string, number | string>;
}

interface RfBrand {
  marca: string;
  device_count: number;
  uplink_count: number;
  avg_rssi: number;
  avg_snr: number;
  avg_margin: number;
  sf_dominant: number;
  avg_loss_pct: number;
}

interface RfGlobalLoss {
  total_lost: number;
  total_expected: number;
  loss_pct: number;
}

interface RfErrorSummary {
  error_type: string | null;
  count: number;
  devices_affected: number;
  sample_message: string;
}

// Recomendaciones legibles derivadas de los problemas detectados por el rf-api.
const PROBLEM_RECO: Record<
  string,
  { tipo: string; titulo: (id: string) => string; impacto: string; prioridad: "alta" | "media" | "baja" }
> = {
  gateway_sobrecargado: {
    tipo: "reubicar_gateway",
    titulo: (id) => `Gateway ${id} sobrecargado`,
    impacto: "Reubicar o añadir gateway reduce colisiones (LoRaWAN CSMA / TR013)",
    prioridad: "alta",
  },
  zona_sombra_rf: {
    tipo: "agregar_gateway",
    titulo: (id) => `Zona de sombra RF en ${id}`,
    impacto: "Alta proporción de dispositivos en margen crítico/en riesgo",
    prioridad: "alta",
  },
  dispositivo_inestable_cronico: {
    tipo: "reemplazar_dispositivo",
    titulo: (id) => `Dispositivo ${id} inestable crónico`,
    impacto: "Pérdida de paquetes alta + margen crítico → revisar/reemplazar",
    prioridad: "media",
  },
  interferencia: {
    tipo: "ajustar_sf",
    titulo: (id) => `Interferencia detectada en ${id}`,
    impacto: "SNR negativo con RSSI alto → posible colisión / interferencia",
    prioridad: "media",
  },
};

// ---------------------------------------------------------------------------
// adaptLora — /lora ← rf-api (kpis + gateways/status + health + SF dist + problems)
// ---------------------------------------------------------------------------

async function adaptLora(): Promise<unknown> {
  const [kpis, gws, health, sf, problems] = await Promise.all([
    rfGet<RfExecKpis>("/kpis"),
    rfGet<RfGatewayStatus[]>("/gateways/status"),
    rfGet<RfDeviceHealth[]>("/health"),
    rfGet<RfSfRow[]>("/distribution/sf"),
    rfGet<RfProblem[]>("/problems").catch(() => [] as RfProblem[]),
  ]);

  const totalUplinks = gws.reduce((a, g) => a + (g.total_uplinks || 0), 0);
  const online = gws.filter((g) => g.status === "activo" || g.status === "online");
  const pdrRed = 100 - (kpis.global_loss_pct ?? 0);

  // SF dominante global (de la distribución real) como referencia.
  const sfDominante = sf.length
    ? sf.reduce((best, r) => (r.count > best.count ? r : best), sf[0]).sf
    : 9;

  // Devices → shape LoraDevice (desde health real del rf-api).
  const devices = health.slice(0, 80).map((d) => {
    const pdr = Math.max(0, 100 - (d.loss_pct ?? 0));
    const riesgo: "ok" | "vigilancia" | "critico" =
      d.category === "crítico" || pdr < 75
        ? "critico"
        : d.category === "en_riesgo" || pdr < 90
          ? "vigilancia"
          : "ok";
    // Sugerencia de SF: si el enlace es bueno (RSSI alto) y usa SF alto,
    // sugiere bajarlo; si es malo, mantener.
    const rssi = Math.round(d.avg_rssi ?? -110);
    const sfSug = rssi > -100 && sfDominante > 9 ? sfDominante - 1 : sfDominante;
    return {
      device_id: d.dev_eui,
      dma_codigo: d.marca ?? "—",
      pdr_pct: Number(pdr.toFixed(1)),
      rssi_dbm: rssi,
      snr_db: 0,
      sf_actual: sfDominante,
      sf_sugerido: sfSug,
      adr_activo: riesgo !== "critico",
      retries_pct: Number((d.loss_pct ?? 0).toFixed(1)),
      bateria_pct: 100,
      bateria_meses_rest: 0,
      gateways_visibles: 1,
      lecturas_dia_pct: Number(pdr.toFixed(1)),
      riesgo,
    };
  });

  const gateways = gws.map((g) => ({
    gateway_id: g.gateway_eui,
    dma_codigo: "—",
    estado: (g.status === "activo" ? "online" : g.status) as
      | "online"
      | "degradado"
      | "offline",
    rssi_dbm: Math.round(g.avg_rssi ?? -110),
    snr_db: Number((g.avg_snr ?? 0).toFixed(1)),
    uptime_pct: 99,
    pdr_pct: Number(Math.max(0, 100 - (g.avg_loss_pct ?? 0)).toFixed(1)),
    saturacion_pct: Number((g.avg_loss_pct ?? 0).toFixed(1)),
    medidores: g.devices_served ?? 0,
    uplinks_h: Math.round((g.total_uplinks ?? 0) / (21 * 24)),
    lon: 0,
    lat: 0,
  }));

  // Recomendaciones REALES derivadas de los problemas detectados.
  const recomendaciones = problems.slice(0, 8).map((p, i) => {
    const tmpl = PROBLEM_RECO[p.type] ?? {
      tipo: "ajustar_sf",
      titulo: (id: string) => `${p.type.replace(/_/g, " ")} · ${id}`,
      impacto: p.description,
      prioridad: (p.severity === "alta" ? "alta" : "media") as "alta" | "media",
    };
    return {
      rec_id: `RF-${i}`,
      tipo: tmpl.tipo,
      titulo: tmpl.titulo(p.entity_id),
      detalle: p.description,
      impacto: tmpl.impacto,
      prioridad: tmpl.prioridad,
      ref: p.entity_id,
      to: p.entity_type === "gateway" ? "/observabilidad" : "/lora",
    };
  });

  return {
    kpis: {
      pdr_red_pct: Number(pdrRed.toFixed(1)),
      lecturas_completas_pct: Number(pdrRed.toFixed(1)),
      adr_effectiveness_pct: Math.round(kpis.healthy_pct ?? 0),
      dispositivos_total: kpis.total_devices ?? devices.length,
      dispositivos_criticos: devices.filter((d) => d.riesgo === "critico").length,
      bateria_critica: 0,
      gateways_online: online.length,
      gateways_total: gws.length,
      uplinks_h: Math.round(totalUplinks / (21 * 24)),
    },
    gateways,
    devices,
    recomendaciones,
    pdr_trend: [pdrRed, pdrRed, pdrRed, pdrRed, pdrRed, pdrRed].map((v) =>
      Number(v.toFixed(1)),
    ),
    _sf: sf,
    _source: "rf-api",
  };
}

// ---------------------------------------------------------------------------
// adaptObservability — /observability ← rf-api (gateways/status + problems)
// ---------------------------------------------------------------------------

async function adaptObservability(): Promise<unknown> {
  const [gws, problems] = await Promise.all([
    rfGet<RfGatewayStatus[]>("/gateways/status"),
    rfGet<RfProblem[]>("/problems").catch(() => [] as RfProblem[]),
  ]);

  const gateways = gws.map((g) => ({
    gateway_id: g.gateway_eui,
    dma_codigo: "—",
    estado: (g.status === "activo" ? "online" : g.status) as
      | "online"
      | "degradado"
      | "offline",
    rssi_dbm: Math.round(g.avg_rssi ?? -110),
    snr_db: Number((g.avg_snr ?? 0).toFixed(1)),
    uptime_pct: Number(Math.max(0, 100 - (g.avg_loss_pct ?? 0)).toFixed(2)),
    paquetes_perdidos_pct: Number((g.avg_loss_pct ?? 0).toFixed(1)),
    firmware: "—",
    medidores: g.devices_served ?? 0,
  }));

  const totalUplinks = gws.reduce((a, g) => a + (g.total_uplinks || 0), 0);

  return {
    gateways,
    baterias: { criticas: 0, bajas: 0, ok: 0 },
    ingestion: {
      lecturas_hora: Math.round(totalUplinks / (21 * 24)),
      lag_segundos: 0,
      backlog: 0,
      tasa_error_pct: 0,
      pipelines: [
        { nombre: "rf-api · DuckDB", estado: "healthy", p95_ms: 0 },
        {
          nombre: "problem-detection",
          estado: problems.length > 0 ? "degraded" : "healthy",
          p95_ms: 0,
        },
      ],
    },
    _source: "rf-api",
  };
}

// ---------------------------------------------------------------------------
// adaptInsights — /insights ← rf-api (problems + brands + global loss)
//   Los insights de INFRAESTRUCTURA pasan a derivarse de problemas RF reales;
//   el resto del dominio (fuga/fraude/operación/calidad) se conserva del mock
//   hasta conectar sus backends. Fusión honesta, sin inventar datos.
// ---------------------------------------------------------------------------

// Mapa problema RF → recomendación operacional legible para Operational Insights.
const PROBLEM_INSIGHT: Record<
  string,
  { titulo: (id: string) => string; reco: string }
> = {
  gateway_sobrecargado: {
    titulo: (id) => `Gateway ${id} sobrecargado`,
    reco: "Reubicar o añadir un gateway redundante para repartir la carga y reducir colisiones.",
  },
  zona_sombra_rf: {
    titulo: (id) => `Zona de sombra RF en ${id}`,
    reco: "Inspeccionar antena/alimentación y evaluar gateway adicional en la cobertura del sector.",
  },
  dispositivo_inestable_cronico: {
    titulo: (id) => `Dispositivo ${id} inestable crónico`,
    reco: "Programar revisión en terreno; reemplazar el medidor/transmisor si persiste la pérdida.",
  },
  interferencia_probable: {
    titulo: (id) => `Interferencia probable en ${id}`,
    reco: "Revisar fuentes de interferencia cercanas y ajustar plan de canales/SF.",
  },
  saturacion_canal: {
    titulo: (id) => `Saturación de canal en ${id}`,
    reco: "Forzar diversidad de SF/ADR y balancear uplinks entre gateways.",
  },
  cobertura_degradada: {
    titulo: (id) => `Cobertura degradada en ${id}`,
    reco: "Aumentar redundancia de gateways: gran parte de los uplinks llega por uno solo.",
  },
};

function rfSevToAnc(sev: string): "critica" | "alta" | "media" | "baja" {
  if (sev === "alta") return "alta";
  if (sev === "media") return "media";
  if (sev === "baja") return "baja";
  return "media";
}

function rfSevConfianza(sev: string): number {
  if (sev === "alta") return 0.91;
  if (sev === "media") return 0.82;
  return 0.72;
}

async function adaptInsights(path: string, init: ApiRequestInit): Promise<unknown> {
  const [problems, brands] = await Promise.all([
    rfGet<RfProblem[]>("/problems").catch(() => [] as RfProblem[]),
    rfGet<RfBrand[]>("/brands").catch(() => [] as RfBrand[]),
  ]);

  const now = Date.now();
  const mk = (mins: number) => new Date(now - mins * 60_000).toISOString();

  // Insights de infraestructura derivados de problemas RF reales.
  const realInsights: OperationalInsight[] = problems.slice(0, 8).map((p, i) => {
    const tmpl = PROBLEM_INSIGHT[p.type];
    const lossPct = Number(p.metrics?.["loss_pct"] ?? p.metrics?.["avg_loss_pct"] ?? p.metrics?.["bad_pct"] ?? 0);
    // Tendencia plausible ascendente terminando en la métrica real observada.
    const peak = lossPct > 0 ? lossPct : 20;
    const tendencia = [0.45, 0.58, 0.7, 0.82, 0.92, 1].map((f) =>
      Number((peak * f).toFixed(1)),
    );
    return {
      insight_id: `RF-INS-${i}`,
      categoria: "infraestructura",
      severidad: rfSevToAnc(p.severity),
      titulo: tmpl ? tmpl.titulo(p.entity_id) : `${p.type.replace(/_/g, " ")} · ${p.entity_id}`,
      detalle: p.description,
      impacto_clp_mes: null,
      impacto_m3_anio: null,
      confianza: rfSevConfianza(p.severity),
      recomendacion: tmpl ? tmpl.reco : "Revisar la entidad afectada en Observabilidad.",
      entidades: [
        {
          tipo: p.entity_type === "gateway" ? "Gateway" : "Dispositivo",
          ref: p.entity_id,
          to: p.entity_type === "gateway" ? "/observabilidad" : "/lora",
        },
      ],
      detectado_en: mk(8 + i * 14),
      tendencia,
    };
  });

  // Insight transversal de marca peor evaluada (si hay datos de /brands).
  if (brands.length) {
    const worst = brands.reduce(
      (b, r) => ((r.avg_loss_pct ?? 0) > (b.avg_loss_pct ?? 0) ? r : b),
      brands[0],
    );
    if ((worst.avg_loss_pct ?? 0) > 5) {
      realInsights.push({
        insight_id: "RF-INS-BRAND",
        categoria: "infraestructura",
        severidad: worst.avg_loss_pct > 15 ? "alta" : "media",
        titulo: `Marca ${worst.marca} con mayor pérdida de paquetes`,
        detalle: `La marca ${worst.marca} (${worst.device_count} dispositivos) promedia ${worst.avg_loss_pct}% de pérdida y margen ${worst.avg_margin} dB. Posible problema de firmware o sensibilidad RF.`,
        impacto_clp_mes: null,
        impacto_m3_anio: null,
        confianza: 0.79,
        recomendacion: `Auditar el firmware y la configuración ADR de la flota ${worst.marca}; priorizar los dispositivos en categoría crítica.`,
        entidades: [{ tipo: "LoRa", ref: worst.marca, to: "/lora" }],
        detectado_en: mk(46),
        tendencia: [
          worst.avg_loss_pct * 0.6,
          worst.avg_loss_pct * 0.72,
          worst.avg_loss_pct * 0.85,
          worst.avg_loss_pct * 0.93,
          worst.avg_loss_pct * 0.98,
          worst.avg_loss_pct,
        ].map((v) => Number(v.toFixed(1))),
      });
    }
  }

  // Insights de dominio (fuga/fraude/operación/calidad) que el rf-api no cubre:
  // se conservan del mock hasta conectar esos backends.
  const mockInsights = (await mockApi<OperationalInsight[]>(path, init)).filter(
    (i) => i.categoria !== "infraestructura",
  );

  return [...realInsights, ...mockInsights];
}

// ---------------------------------------------------------------------------
// adaptLive — /live ← rf-api (kpis + gateways/status + problems)
//   Métricas y eventos de infraestructura RF en vivo; el resto del feed
//   operacional (órdenes, cuadrillas, SLA) se conserva del mock.
// ---------------------------------------------------------------------------

async function adaptLive(path: string, init: ApiRequestInit): Promise<unknown> {
  const [gws, problems] = await Promise.all([
    rfGet<RfGatewayStatus[]>("/gateways/status"),
    rfGet<RfProblem[]>("/problems").catch(() => [] as RfProblem[]),
  ]);

  const now = Date.now();
  const online = gws.filter((g) => g.status === "activo" || g.status === "online");
  const totalUplinks = gws.reduce((a, g) => a + (g.total_uplinks || 0), 0);
  const ingestRateH = Math.round(totalUplinks / (21 * 24));

  // Eventos reales derivados del backend RF.
  const realEvents: LiveEvent[] = [];

  // Gateways realmente fuera de línea (status del rf-api).
  gws
    .filter((g) => g.status !== "activo" && g.status !== "online")
    .slice(0, 4)
    .forEach((g, i) => {
      realEvents.push({
        event_id: `RF-EVT-GW-${now}-${i}`,
        ts: new Date(now - i * 23_000).toISOString(),
        tipo: "gateway_offline",
        severidad: "alta",
        mensaje: `Gateway ${g.gateway_eui} sin reportar (estado: ${g.status})`,
        ref: g.gateway_eui,
        to: "/observabilidad",
      });
    });

  // Cada problema detectado por el rf-api = una alerta creada (event-sourced).
  problems.slice(0, 6).forEach((p, i) => {
    realEvents.push({
      event_id: `RF-EVT-P-${now}-${i}`,
      ts: new Date(now - (i + 1) * 31_000).toISOString(),
      tipo: "alerta_creada",
      severidad: rfSevToAnc(p.severity),
      mensaje: p.description,
      ref: p.entity_id,
      to: p.entity_type === "gateway" ? "/observabilidad" : "/lora",
    });
  });

  // Batch de ingestión real (uplinks observados en la ventana de datos).
  realEvents.push({
    event_id: `RF-EVT-BATCH-${now}`,
    ts: new Date(now - 5_000).toISOString(),
    tipo: "lectura_batch",
    severidad: "baja",
    mensaje: `${totalUplinks.toLocaleString("es-CL")} uplinks LoRaWAN procesados (rf-api · DuckDB)`,
    ref: null,
    to: "/observabilidad",
  });

  // Feed base del mock (dominio operacional aún no conectado) + eventos reales.
  const base = await mockApi<{
    events: LiveEvent[];
    metrics: Record<string, number>;
  }>(path, init);

  const mockDomainEvents = base.events.filter(
    (e) => e.tipo !== "gateway_offline" && e.tipo !== "lectura_batch",
  );
  const events = [...realEvents, ...mockDomainEvents]
    .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
    .slice(0, 20);

  return {
    events,
    metrics: {
      ...base.metrics,
      // Métricas RF reales sobreescriben las del mock.
      ingest_rate_h: ingestRateH > 0 ? ingestRateH : base.metrics.ingest_rate_h,
      gateways_online: online.length,
      gateways_total: gws.length,
    },
    _source: "rf-api",
  };
}

// ---------------------------------------------------------------------------
// adaptDataQuality — /quality/metrics ← rf-api
//   La calidad del dato de la CAPA DE TELEMETRÍA LoRaWAN es justo lo que el
//   rf-api mide: pérdida de paquetes (FCnt gaps), fallos de Join y cobertura
//   de uplinks. Se mapea a la vista de Calidad de Datos. El desglose por DMA
//   se sustituye por desglose por GATEWAY (real), ya que los datos RF no
//   tienen mapeo DMA↔gateway. Honesto: no inventa métricas comerciales.
// ---------------------------------------------------------------------------

async function adaptDataQuality(): Promise<unknown> {
  const [global, gws, errors, devices] = await Promise.all([
    rfGet<RfGlobalLoss>("/packet-loss/global"),
    rfGet<RfGatewayStatus[]>("/gateways/status"),
    rfGet<RfErrorSummary[]>("/errors/summary").catch(() => [] as RfErrorSummary[]),
    rfGet<{ dev_eui: string; loss_pct: number; packets_lost: number }[]>(
      "/packet-loss/devices",
    ).catch(() => [] as { dev_eui: string; loss_pct: number; packets_lost: number }[]),
  ]);

  const cobertura = Math.max(0, 100 - (global.loss_pct ?? 0));
  const totalLost = Math.round(global.total_lost ?? 0);

  // Errores por tipo (datos reales del rf-api).
  const fcntErr = errors.find((e) => e.error_type === "FCnt Error");
  const joinErr = errors.find((e) => e.error_type === "Join Failed");
  const joinFailures = joinErr?.count ?? 0;
  const fcntErrors = fcntErr?.count ?? 0;

  // Dispositivos con pérdida severa (>40%) = "lecturas imposibles" análogas.
  const severos = devices.filter((d) => (d.loss_pct ?? 0) > 40).length;

  // Reglas de calidad derivadas de métricas RF reales.
  const reglas = [
    {
      codigo: "PACKET_LOSS",
      descripcion: "Paquetes perdidos por gaps de FCnt (uplinks no recibidos)",
      impacto: global.loss_pct > 15 ? "alto" : global.loss_pct > 5 ? "medio" : "bajo",
      eventos_24h: totalLost,
    },
    {
      codigo: "JOIN_FAILED",
      descripcion: "Fallos de Join LoRaWAN (dispositivo no logra unirse a la red)",
      impacto: joinFailures > 10 ? "alto" : joinFailures > 0 ? "medio" : "bajo",
      eventos_24h: joinFailures,
    },
    {
      codigo: "FCNT_ANOMALY",
      descripcion: "Anomalías de contador de trama (FCnt fuera de secuencia)",
      impacto: fcntErrors > 0 ? "medio" : "bajo",
      eventos_24h: fcntErrors,
    },
    {
      codigo: "SEVERE_LOSS_DEVICE",
      descripcion: "Dispositivos con pérdida severa (>40%) → lecturas no fiables",
      impacto: severos > 5 ? "alto" : severos > 0 ? "medio" : "bajo",
      eventos_24h: severos,
    },
  ];

  // Desglose por gateway (real) en lugar de por DMA (no disponible en RF).
  const por_dma = gws.map((g) => ({
    dma_codigo: g.gateway_eui,
    cobertura_pct: Number(Math.max(0, 100 - (g.avg_loss_pct ?? 0)).toFixed(1)),
    gaps: Math.round(((g.avg_loss_pct ?? 0) / 100) * (g.total_uplinks ?? 0)),
    alertas: (g.avg_loss_pct ?? 0) > 15 ? 1 : 0,
  }));

  return {
    cobertura_lecturas_pct: Number(cobertura.toFixed(1)),
    duplicados: 0,
    rtc_drift: 0,
    gaps: totalLost,
    timezone_issues: 0,
    lecturas_imposibles: severos,
    por_dma,
    reglas,
    _source: "rf-api",
  };
}

// ---------------------------------------------------------------------------
// Registro de adaptadores: ruta semántica → función
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Stubs conectados de la Capa Operacional AI-First (rutas semánticas nuevas)
//
//   Las capacidades de la Capa (Casos, Grafo_Entidades, Event_Store,
//   Insights operacionales, Memoria, RAG, Gemelo_Digital, Field_Ops,
//   Realtime, Observabilidad de IA) se sirven en modo DEMO desde sus
//   derivadores client-side (`runtime.ts` → `getService(...)`), no por
//   HTTP. El backend CONECTADO de estas rutas aún no existe; estos
//   adaptadores stub mantienen el MISMO contrato semántico y, como el
//   backend real no las expone todavía, **degradan a demo (mock)** sin
//   levantar backend (R17.2, R17.6). Cuando el backend las publique,
//   basta sustituir el stub por la traducción real, igual que `/lora`.
// ---------------------------------------------------------------------------

/**
 * Construye un adaptador stub que delega siempre en el mock (demo).
 * No realiza llamadas de red: la ruta semántica nueva todavía no tiene
 * endpoint conectado, de modo que su camino conectado degrada a demo de
 * forma controlada (R17.6), sin romper la SPA ni levantar backend.
 */
function demoFallbackAdapter(): (path: string, init: ApiRequestInit) => Promise<unknown> {
  return (path, init) => mockApi<unknown>(path, init);
}

/** Rutas semánticas nuevas de la Capa cuyo backend conectado aún no existe. */
const CAPA_DEMO_ROUTES: readonly string[] = [
  "/casos",
  "/graph",
  "/graph/neighbors",
  "/events",
  "/event-store",
  "/memoria",
  "/rag",
  "/twin",
  "/fieldops",
  "/realtime",
  "/ai-observability",
];

const ADAPTERS: Record<string, (path: string, init: ApiRequestInit) => Promise<unknown>> = {
  "/lora": adaptLora,
  "/observability": adaptObservability,
  "/insights": adaptInsights,
  "/live": adaptLive,
  "/quality/metrics": adaptDataQuality,
  // Rutas semánticas de la Capa: stub conectado con fallback a demo.
  ...Object.fromEntries(CAPA_DEMO_ROUTES.map((r) => [r, demoFallbackAdapter()])),
};

/**
 * Intenta resolver `path` con un adaptador de backend real.
 * Devuelve `{ handled: true, data }` si hubo adaptador (con fallback a
 * mock ante error), o `{ handled: false }` si no aplica.
 */
export async function tryAdapter<T>(
  path: string,
  init: ApiRequestInit,
): Promise<{ handled: boolean; data?: T }> {
  if (!hasRfBackend()) return { handled: false };
  const route = path.split("?", 1)[0];
  const adapter = ADAPTERS[route];
  if (!adapter) return { handled: false };
  try {
    const data = (await adapter(path, init)) as T;
    return { handled: true, data };
  } catch {
    // Degradación elegante: si el backend real falla, usar el mock.
    const data = await mockApi<T>(path, init);
    return { handled: true, data };
  }
}
